import React, { useState, useRef, useContext, useEffect, useCallback, useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Spin, Grid, Select, Modal, Descriptions, Tag } from "antd";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import "./AttendanceCalendar.css";
import ImproperLog from "../ImproperLog";


export default function LeaveCalendar({ employee }) {
    const { authData, backUrl } = useContext(AuthContext);
    const calendarRef = useRef(null);
    const containerRef = useRef(null);
    const [loading, setLoading] = useState(false);

    const MIN_YEAR = 2026;
    const MIN_MONTH = 2;

    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const [leaveData, setLeaveData] = useState({});
    const leaveDataRef = useRef({});
    const loadingKeysRef = useRef(new Set());

    /* ---------------------------------------------- */
    /* DATE CLICK MODAL STATE                          */
    /* ---------------------------------------------- */
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedDateInfo, setSelectedDateInfo] = useState(null); // { date, record }

    const { useBreakpoint } = Grid;
    const screens = useBreakpoint();

    const employeeId =
        employee?.employee?.id ||
        employee?.employee_id ||
        employee?.id ||
        authData?.employee?.id ||
        authData?.employeeId ||
        authData?.user?.employee_id ||
        authData?.user?.id ||
        null;

    /* ---------------------------------------------- */
    /* STATUS COLORS (RESTORED EXACT ORIGINAL)        */
    /* ---------------------------------------------- */

    const statusColors = {
        PRESENT: "#00AF50",                        // dark green
        ABSENT: "#FE0000",                         // red
        "GRACE - LATE IN": "#FFFECB",
        "GRACE - EARLY OUT": "#FFFECB",             // light mint green
        "GRACE – EARLY OUT": "#FFFECB",           // light mint green
        "LATE IN": "#FFFECB",                      // light yellow
        "EARLY OUT": "#FFFECB",                    // light yellow
        "SHORTFALL PRESENT": "#298988",            // dark teal
        "HALF DAY - SHORTFALL PRESENT": "#58BABB", // medium teal/cyan
        "HALFDAY SHORTFALL": "#58BABB",            // medium teal/cyan
        "SHORT HOURS": "#DED9C5",                  // beige/tan
        "MISPUNCH OUT": "#BFBFBF",                 // medium gray
        "MISPUNCH IN": "#BFBFBF",                  // medium gray
        PERMISSION: "#98FF66",                     // bright lime green
        HOLIDAY: "#F79647",                        // orange
        SUNDAY: "#FFFF00",                         // pure yellow
        COMPOFF: "#1FA2CC",                        // sky/cyan blue
        DEFAULT: "#ffffff",
        WEEKOFF: "#FFFF00",                        // pure yellow
        "WEEK OFF": "#FFFF00",
        "WEEK_OFF": "#FFFF00",
        WO: "#FFFF00",
        "OFF DAY": "#FFFF00",
        TECHNICAL_ISSUE: "#303343",
        CASUAL: "#CB00CC",                         // magenta/purple
        SICK: "#FF0066",                           // hot pink / deep pink
        "HALF DAY - CASUAL LEAVE": "#FE66CB",     // light pink
        "HALF DAY - SICK LEAVE": "#B1A1C8",
        "HALF DAY - CASUAL": "#FE66CB",             // light pink
        "HALF DAY - SICK": "#B1A1C8",
        HALFDAYLEAVE: "#FE66CB",
        HALF_DAY_LEAVE: "#FE66CB",
        HALFDAY_LEAVE: "#FE66CB",
        "HALF DAY LEAVE": "#FE66CB",
        LOP: "#bf4406",
        "HALF DAY - COMPOFF": "#66ffff",
        "HALF DAY - WEEKOFF": "#ffcc00",
        "GENERAL PERMISSION": "#4F46E5",           // royal indigo (distinct, non-colliding)
        GENERAL_PERMISSION: "#4F46E5",
        "GEN PERM": "#4F46E5",
        GEN_PERM: "#4F46E5",
    };

    /* Helper function to check if record is General Permission / Grace Rule */
    const isGeneralPermission = (status, secondary, remarks) => {
        const str = `${status || ""} ${secondary || ""} ${remarks || ""}`.toUpperCase();
        return (
            str.includes("GENERAL PERMISSION") ||
            str.includes("GENERAL_PERMISSION") ||
            str.includes("SPECIAL PERMISSION") ||
            str.includes("PERMISSION CONSIDERATION") ||
            str.includes("GEN PERM")
        );
    };

    /* Helper function to resolve exact event color including Half Day variants */
    const getEventColor = useCallback((status, secondary, remarks) => {
        const s = (status || "").toUpperCase();
        const r = (remarks || "").toUpperCase();
        const sec = (secondary || "").toUpperCase();

        // 0. General Permission Check (Distinct Royal Indigo #4F46E5)
        if (isGeneralPermission(s, sec, r)) {
            return "#4F46E5";
        }

        // Clean remarks of "on behalf of" so "behalf" does not match "HALF"
        const rClean = r.replace(/\bON BEHALF OF\b/g, "").replace(/\bBEHALF\b/g, "");

        // 1. Week Off / Sunday Matching
        if (s.includes("WEEK") || rClean.includes("WEEK") || s.includes("OFF DAY") || rClean.includes("OFF DAY") || s === "WO" || sec.includes("WEEK") || s === "SUNDAY" || rClean === "SUNDAY") {
            if (s.includes("HALF") || rClean.includes("HALF")) return "#ffcc00"; // Half Day Week Off
            return "#FFFF00"; // Pure Yellow Week Off
        }

        // 2. Comp Off
        if (s.includes("COMP") || rClean.includes("COMP") || sec.includes("COMP")) {
            if (s.includes("HALF") || rClean.includes("HALF")) return "#66ffff"; // Half Day Comp Off
            return "#1FA2CC"; // Sky Blue Comp Off
        }

        // 3. Shortfall Present (Evaluated before generic Half Day)
        if (s.includes("SHORTFALL") || rClean.includes("SHORTFALL") || sec.includes("SHORTFALL")) {
            if (s.includes("HALF") || rClean.includes("HALF") || sec.includes("HALF")) return "#58BABB"; // Medium teal
            return "#298988"; // Dark teal
        }
        if (rClean === "HALF DAY - SICK" || rClean === "HALF DAY - SICK LEAVE" || (sec.includes("SICK") && (s.includes("HALF") || rClean.includes("HALF")))) {
            return "#B1A1C8"; // Half Day Sick
        }
        if (rClean === "HALF DAY - CASUAL" || rClean === "HALF DAY - CASUAL LEAVE" || (sec.includes("CASUAL") && (s.includes("HALF") || rClean.includes("HALF")))) {
            return "#FE66CB"; // Half Day Casual
        }
        if (s.includes("HALF") || rClean.includes("HALF")) {
            return "#FE66CB"; // Generic Half Day
        }

        // 4. Casual Leave
        if (sec.includes("CASUAL") || s.includes("CASUAL") || rClean.includes("CASUAL")) {
            return "#CB00CC";
        }

        // 5. Sick Leave
        if (sec.includes("SICK") || s.includes("SICK") || rClean.includes("SICK")) {
            return "#FF0066";
        }

        // 6. Shortfall Present
        if (s.includes("SHORTFALL") || rClean.includes("SHORTFALL")) {
            if (s.includes("HALF") || rClean.includes("HALF")) return "#58BABB";
            return "#298988";
        }

        // 7. Permission
        if (s.includes("PERMISSION") || rClean.includes("PERMISSION") || sec.includes("PERMISSION")) {
            return "#98FF66";
        }

        // 8. Late In / Grace / Early Out
        if (s.includes("LATE") || rClean.includes("LATE") || s.includes("GRACE") || rClean.includes("GRACE") || s.includes("EARLY") || rClean.includes("EARLY")) {
            return "#FFFECB";
        }

        return statusColors[sec] || statusColors[rClean] || statusColors[r] || statusColors[s] || statusColors.DEFAULT;
    }, []);

    /* Tag color mapping for Ant Design Tag component */
    const statusTagColor = {
        PRESENT: "success",
        ABSENT: "error",
        LEAVE: "error",
        HALF_DAY_LEAVE: "warning",
        HALFDAYLEAVE: "warning",
        PERMISSION: "cyan",
        HOLIDAY: "orange",
        SUNDAY: "gold",
        WEEKOFF: "gold",
        COMPOFF: "blue",
        LOP: "red",
        TECHNICAL_ISSUE: "default",
        DEFAULT: "default",
    };

    const statusLabel = {
        PRESENT: "Present",
        ABSENT: "Absent",
        LEAVE: "Leave",
        HALF_DAY_LEAVE: "Half Day Leave",
        HALFDAYLEAVE: "Half Day Leave",
        PERMISSION: "Permission",
        HOLIDAY: "Holiday",
        SUNDAY: "Week Off",
        WEEKOFF: "Week Off",
        COMPOFF: "Comp Off",
        LOP: "Loss of Pay",
        TECHNICAL_ISSUE: "Technical Issue",
        DEFAULT: "No Record",
    };

    const getDarkerColor = (hex) => {
        const namedToHex = {
            green: "#008000", red: "#ff0000", lightgreen: "#90ee90",
            orange: "#ffa500", yellow: "#ffff00", blue: "#0000ff",
            white: "#ffffff", gray: "#808080",
        };
        let h = namedToHex[hex] || hex;
        const amt = -60;
        let usePound = false;
        if (h[0] === "#") { h = h.slice(1); usePound = true; }
        let num = parseInt(h, 16);
        let r = Math.max(Math.min(255, (num >> 16) + amt), 0);
        let g = Math.max(Math.min(255, ((num >> 8) & 0x00ff) + amt), 0);
        let b = Math.max(Math.min(255, (num & 0x0000ff) + amt), 0);
        return (usePound ? "#" : "") + ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0");
    };

    /* ---------------------------------------------- */
    /* FORCE SIZE RECALCULATION (modal/tab fix)        */
    /* ---------------------------------------------- */
    const updateCalendarSize = useCallback(() => {
        calendarRef.current?.getApi()?.updateSize();
    }, []);

    useEffect(() => {
        if (!containerRef.current) return;
        const t1 = setTimeout(updateCalendarSize, 0);
        const t2 = setTimeout(updateCalendarSize, 150);
        const t3 = setTimeout(updateCalendarSize, 400);
        const observer = new ResizeObserver(updateCalendarSize);
        observer.observe(containerRef.current);
        return () => {
            clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
            observer.disconnect();
        };
    }, [updateCalendarSize]);

    /* ---------------------------------------------- */
    /* YEAR OPTIONS                                    */
    /* ---------------------------------------------- */
    const yearOptions = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i)
        .filter((y) => y >= MIN_YEAR);

    /* ---------------------------------------------- */
    /* LOAD LEAVES                                     */
    /* ---------------------------------------------- */
    const loadLeaves = useCallback(async (month, year) => {
        if (!employeeId) return;
        const key = `${year}-${month}`;
        if (leaveDataRef.current[key] !== undefined) return;
        if (loadingKeysRef.current.has(key)) return;

        loadingKeysRef.current.add(key);
        setLoading(true);
        try {
            const res = await axios.get(
                `${backUrl}/api/hr/calendar/?employee_id=${employeeId}&month=${month}&year=${year}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            const data = res.data.results || res.data || [];
            leaveDataRef.current[key] = data;
            setLeaveData((prev) => ({ ...prev, [key]: data }));
        } catch (err) {
            console.error("Error fetching leave data:", err);
            leaveDataRef.current[key] = [];
            setLeaveData((prev) => ({ ...prev, [key]: [] }));
        } finally {
            setLoading(false);
            loadingKeysRef.current.delete(key);
        }
    }, [employeeId, backUrl, authData?.access]);

    /* ---------------------------------------------- */
    /* BUILD EVENTS                                    */
    /* ---------------------------------------------- */
    const buildEvents = () => {
        const events = [];
        Object.values(leaveData).forEach((records) => {
            (records || []).forEach((item) => {
                const date = item?.Date || item?.date;
                if (!date) return;

                const status = item?.Status?.toUpperCase?.() || item?.AttendanceStatus?.toUpperCase?.() || "DEFAULT";
                const leaveType = item?.LeaveType || null;
                const remarks = item?.StatusMessage || item?.remarks || item?.AttendanceStatus || "DEFAULT";
                const rawStatusMessage = item?.StatusMessage || item?.remarks || item?.AttendanceStatus || null;
                const color = getEventColor(status, leaveType, remarks);

                events.push({
                    start: date,
                    display: "background",
                    backgroundColor: color,
                    borderColor: color,
                    extendedProps: { status, secondary: leaveType, remarks, rawStatusMessage, rawRecord: item },
                });
            });
        });
        return events;
    };

    /* ---------------------------------------------- */
    /* MONTH ATTENDANCE SUMMARY METRICS               */
    /* ---------------------------------------------- */
    const monthStats = useMemo(() => {
        let present = 0;
        let absent = 0;
        let weekoff = 0;
        let holiday = 0;
        let leave = 0;

        const key = `${selectedYear}-${String(currentMonth).padStart(2, '0')}`;
        const currentRecords = leaveData[key] || [];
        currentRecords.forEach((item) => {
            const status = (item?.AttendanceStatus || "").toUpperCase();
            const remarks = (item?.StatusMessage || "").toUpperCase();

            if (status === "PRESENT" || remarks === "PRESENT") present++;
            else if (status === "ABSENT" || status === "LOP" || remarks === "ABSENT") absent++;
            else if (status === "WEEKOFF" || status === "SUNDAY" || remarks === "WEEKOFF") weekoff++;
            else if (status === "HOLIDAY" || remarks === "HOLIDAY") holiday++;
            else if (status.includes("LEAVE") || status.includes("CASUAL") || status.includes("SICK")) leave++;
        });

        return { present, absent, weekoff, holiday, leave };
    }, [leaveData, selectedYear, currentMonth]);

    /* ---------------------------------------------- */
    /* EVENT CONTENT                                   */
    /* ---------------------------------------------- */

    const eventContent = (eventInfo) => {
        const { status, secondary, remarks } = eventInfo.event.extendedProps;
        const color = getEventColor(status, secondary, remarks);
        const isGenPerm = isGeneralPermission(status, secondary, remarks);

        // Sanitize remarks to prevent "behalf" from matching "HALF"
        const rClean = (remarks || "").toUpperCase().replace(/\bON BEHALF OF\b/g, "").replace(/\bBEHALF\b/g, "");
        const sUpper = (status || "").toUpperCase();
        const secUpper = (secondary || "").toUpperCase();

        const isHalfDay = !isGenPerm && (
            sUpper.includes("HALF") ||
            rClean.includes("HALF") ||
            secUpper.includes("HALF") ||
            status === "HALFDAYLEAVE" ||
            status === "HALF_DAY_LEAVE" ||
            status === "HALFDAY_LEAVE"
        );

        // Resolve the display label for this calendar cell
        const resolveDisplayText = () => {
            const s = (status || "").toUpperCase();
            const r = (remarks || "").toUpperCase();
            const sec = (secondary || "").toUpperCase();

            if (isGenPerm) return "Gen Perm";
            if (status === "LOP") return "NLB";

            if (isHalfDay) {
                if (r.includes("SHORT HOURS") || r === "SHORT HOURS") return "HD - Short Hours";
                if (r.includes("SHORTFALL") || s.includes("SHORTFALL")) return "HD - Shortfall";
                if (sec.includes("SICK") || r.includes("SICK")) return "HD - SL";
                if (sec.includes("CASUAL") || r.includes("CASUAL")) return "HD - CASUAL";
                if (sec.includes("COMP") || r.includes("COMP")) return "HD - COMP";
                if (sec.includes("WEEK") || r.includes("WEEK")) return "HD - WEEK";
                return "HL";
            }

            if (sec.includes("COMP") || s.includes("COMP") || r.includes("COMP")) return "CompOff";
            if (sec.includes("WEEK") || s.includes("WEEK") || s.includes("OFF DAY") || r.includes("OFF DAY") || s === "WO" || s === "SUNDAY") return "Weekoff";
            if (sec.includes("SICK") || s.includes("SICK") || r.includes("SICK")) return "SL";
            if (sec.includes("CASUAL") || s.includes("CASUAL") || r.includes("CASUAL")) return "CL";
            if (r.includes("EARLY OUT") || r.includes("EARLY")) return "Early Out";
            if (r.includes("LATE IN") || r.includes("LATE")) return "Late In";

            const text = {
                PRESENT: "Present",
                ABSENT: "Absent",
                LEAVE: "Leave",
                PERMISSION: "Perm",
                HOLIDAY: "Holiday",
                TECHNICAL_ISSUE: "TI",
            }[status] || {
                PRESENT: "Present",
                ABSENT: "Absent",
                LEAVE: "Leave",
                HOLIDAY: "Holiday",
                PERMISSION: "Perm",
            }[remarks];

            return text || null;
        };

        const labelText = resolveDisplayText();
        if (!labelText) return null;

        const r_upper = (remarks || "").toUpperCase();
        const s_upper = (status || "").toUpperCase();
        const leftHalfColor = (r_upper.includes("SHORT HOURS") || r_upper.includes("SHORTFALL") || r_upper.includes("MISPUNCH") || s_upper === "ABSENT")
            ? (r_upper.includes("SHORT HOURS") ? "#DED9C5" : "#58BABB")
            : "#00AF50";

        // Dual 50/50 split bar for Half Day (Left: worked half status; Right: leave color)
        const topStyle = isHalfDay
            ? { background: `linear-gradient(90deg, ${leftHalfColor} 50%, ${color} 50%)` }
            : { backgroundColor: color };

        return (
            <div className="fc-event-wrapper">
                <div className="fc-event-top" style={topStyle} />
                <div className="fc-event-bottom">
                    <span className="primary">
                        {labelText}
                    </span>
                </div>
            </div>
        );
    };


    const handleDateClick = useCallback((info) => {
        const clickedDate = info.dateStr; // "YYYY-MM-DD"

        // Prevent modal for future dates (parse as local date to avoid UTC offset issues)
        const today = new Date();
        const [y, m, d] = clickedDate.split("-").map(Number);
        const clicked = new Date(y, m - 1, d); // local midnight
        if (clicked > today) return;

        // Find the record for this date across all loaded data
        let matchedRecord = null;
        for (const records of Object.values(leaveDataRef.current)) {
            const found = (records || []).find((item) => {
                const d = item?.Date || item?.date;
                return d === clickedDate;
            });
            if (found) { matchedRecord = found; break; }
        }

        setSelectedDateInfo({ date: clickedDate, record: matchedRecord });
        setModalOpen(true);
    }, []);

    /* ---------------------------------------------- */
    /* SHARED NAVIGATE HELPER                          */
    /* ---------------------------------------------- */
    const navigateTo = useCallback((month, year) => {
        let safeYear = year;
        let safeMonth = month;
        if (safeYear < MIN_YEAR || (safeYear === MIN_YEAR && safeMonth < MIN_MONTH)) {
            safeYear = MIN_YEAR;
            safeMonth = MIN_MONTH;
        }
        calendarRef.current?.getApi()?.gotoDate(
            `${safeYear}-${String(safeMonth).padStart(2, "0")}-01`
        );
        setCurrentMonth(safeMonth);
        setSelectedYear(safeYear);
        const key = `${safeYear}-${safeMonth}`;
        if (leaveDataRef.current[key] === undefined) {
            loadLeaves(safeMonth, safeYear);
        }
    }, [loadLeaves]);

    /* ---------------------------------------------- */
    /* HANDLE PREV/NEXT BUTTON CHANGES                 */
    /* ---------------------------------------------- */
    const handleDatesSet = useCallback((info) => {
        const start = info.view.currentStart;
        const newMonth = start.getMonth() + 1;
        const newYear = start.getFullYear();

        if (newYear < MIN_YEAR || (newYear === MIN_YEAR && newMonth < MIN_MONTH)) {
            calendarRef.current?.getApi()?.gotoDate(
                `${MIN_YEAR}-${String(MIN_MONTH).padStart(2, "0")}-01`
            );
            return;
        }

        setCurrentMonth(newMonth);
        setSelectedYear(newYear);

        const key = `${newYear}-${newMonth}`;
        if (leaveDataRef.current[key] === undefined) {
            loadLeaves(newMonth, newYear);
        }
    }, [loadLeaves]);

    /* ---------------------------------------------- */
    /* YEAR DROPDOWN CHANGE                            */
    /* ---------------------------------------------- */
    const handleYearChange = useCallback((year) => {
        navigateTo(currentMonth, year);
    }, [currentMonth, navigateTo]);

    /* ---------------------------------------------- */
    /* INITIAL LOAD                                    */
    /* ---------------------------------------------- */
    useEffect(() => {
        if (!employeeId) return;
        const m = new Date().getMonth() + 1;
        const y = new Date().getFullYear();
        setCurrentMonth(m);
        setSelectedYear(y);
        loadLeaves(m, y);
    }, [employeeId]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ---------------------------------------------- */
    /* MODAL CONTENT HELPERS                           */
    /* ---------------------------------------------- */
    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        const d = new Date(dateStr);
        return d.toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    };

    const getModalRecord = () => {
        if (!selectedDateInfo) return null;
        const { record } = selectedDateInfo;
        if (!record) return null;
        const status = record?.Status?.toUpperCase?.() || record?.AttendanceStatus?.toUpperCase?.() || "DEFAULT";
        const remarks = record?.StatusMessage || record?.remarks || record?.LeaveType || record?.HolidayName || record?.AttendanceStatus || null;
        return { status, record, remarks };
    };

    /* ---------------------------------------------- */
    /* RENDER                                          */
    /* ---------------------------------------------- */
    return (
        <div className="w-100">
            <div
                ref={containerRef}
                className={`leave-calendar-container ${screens.xs ? "xs" : screens.sm ? "sm" : "lg"}`}
                style={{ position: "relative" }}
            >
                {/* ------------------ YEAR FILTER (Aligned in Header Row) ------------------ */}
                <div style={{ position: "absolute", top: 2, right: 0, zIndex: 10, display: "flex", alignItems: "center", gap: 6 }}>
                    <label style={{ fontWeight: 700, color: "#0e497b", fontSize: 12, margin: 0 }}>YEAR :</label>
                    <Select
                        value={selectedYear}
                        style={{ width: 92 }}
                        size="small"
                        onChange={handleYearChange}
                    >
                        {yearOptions.map((y) => (
                            <Select.Option key={y} value={y}>{y}</Select.Option>
                        ))}
                    </Select>
                </div>

                {/* ------------------ CALENDAR ------------------ */}
                {loading && Object.keys(leaveData).length === 0 ? (
                    <div className="leave-calendar-loading">
                        <Spin tip="Loading Leave Calendar..." />
                    </div>
                ) : (
                    <>
                        <FullCalendar
                            ref={calendarRef}
                            plugins={[dayGridPlugin, interactionPlugin]}
                            initialView="dayGridMonth"
                            headerToolbar={{
                                left: screens.xs ? "prev,next" : "prev,next today",
                                center: "title",
                                right: "",
                            }}
                            height="auto"
                            aspectRatio={2.1}
                            events={buildEvents()}
                            eventContent={eventContent}
                            eventDisplay="block"
                            displayEventTime={false}
                            datesSet={handleDatesSet}
                            dateClick={handleDateClick}
                            selectable={true}
                            dayCellClassNames={(arg) => {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                return arg.date > today ? ["fc-day-future-disabled"] : [];
                            }}
                        />

                        {loading && (
                            <div style={{ textAlign: "center", marginTop: 4, color: "#888", fontSize: 12 }}>
                                <Spin size="small" />&nbsp; Loading...
                            </div>
                        )}

                        {/* ---------- LEGEND SECTION ---------- */}
                        <div
                            style={{
                                marginTop: 16,
                                marginBottom: 8,
                                padding: "16px 18px",
                                background: "#f8fafc",
                                borderRadius: 14,
                                width: "100%",
                                textAlign: "left",
                                border: "1px solid #e2e8f0",
                            }}
                        >
                            <div style={{ fontSize: 12.5, fontWeight: 800, color: "#0e497b", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 12 }}>
                                🎨 Attendance Status Color Code Legend
                            </div>

                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fill, minmax(165px, 1fr))",
                                    rowGap: 10,
                                    columnGap: 14,
                                    fontSize: 13,
                                }}
                            >
                                {[
                                    { label: "Present", color: "#00AF50" },
                                    { label: "Permission", color: "#98FF66" },
                                    { label: "General Permission", color: "#4F46E5" },
                                    { label: "Late In / Early Out", color: "#FFFECB" },
                                    { label: "Shortfall Present", color: "#298988" },
                                    { label: "HalfDay Shortfall", color: "#58BABB" },
                                    { label: "Week Off", color: "#FFFF00" },
                                    { label: "Comp Off", color: "#1FA2CC" },
                                    { label: "Sick Leave", color: "#FF0066" },
                                    { label: "Casual Leave", color: "#CB00CC" },
                                    { label: "Half Day Sick", color: "#B1A1C8" },
                                    { label: "Half Day Casual", color: "#FE66CB" },
                                    { label: "Short Hours", color: "#DED9C5" },
                                    { label: "Mispunch IN / OUT", color: "#BFBFBF" },
                                    { label: "Absent", color: "#FF0000" },
                                    { label: "Holiday", color: "#FFA040" },
                                    { label: "Loss of Pay (LOP)", color: "#bf4406" },
                                    { label: "Half Day Comp Off", color: "#66ffff" },
                                    { label: "Half Day Week Off", color: "#ffcc00" },
                                    { label: "Technical Issue", color: "#474851" }
                                ].map((item) => (
                                    <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span
                                            style={{
                                                width: 12,
                                                height: 12,
                                                background: item.color,
                                                borderRadius: "50%",
                                                border: "1px solid rgba(0,0,0,0.2)",
                                                display: "inline-block",
                                                flexShrink: 0,
                                            }}
                                        />
                                        <span style={{ color: "#0f172a", fontWeight: 700, fontSize: 13 }}>{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* ================================================ */}
            {/* DATE DETAIL MODAL                                 */}
            {/* ================================================ */}
            <Modal
                title={
                    <div style={{ paddingBottom: 6 }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>Attendance Log</div>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: "#64748b", marginTop: 2 }}>
                            {selectedDateInfo ? formatDate(selectedDateInfo.date) : ""}
                        </div>
                    </div>
                }
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                footer={null}
                centered
                width={580}
                destroyOnClose
            >
                {(() => {
                    const info = getModalRecord();
                    const status = info?.status || "DEFAULT";
                    const record = info?.record;
                    const remarks = info?.remarks;

                    return (
                        <div style={{ paddingTop: 8 }}>
                            {/* Status micro-bar */}
                            <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                <div className="d-flex align-items-center gap-1.5">
                                    <span style={{ fontWeight: 700, color: "#64748b", fontSize: 12 }}>Status:</span>
                                    <Tag
                                        color={statusTagColor[status] || "default"}
                                        style={{ fontSize: 11.5, fontWeight: 800, padding: "2px 8px", borderRadius: 6, margin: 0 }}
                                    >
                                        {status || "No Record"}
                                    </Tag>
                                </div>

                                <div className="d-flex align-items-center gap-1.5">
                                    <span style={{ fontWeight: 700, color: "#64748b", fontSize: 12 }}>Remarks:</span>
                                    <Tag
                                        color={isGeneralPermission(status, null, remarks) ? "#4F46E5" : (statusTagColor[status] || "default")}
                                        style={{
                                            fontSize: 11.5,
                                            fontWeight: 800,
                                            padding: "2px 8px",
                                            borderRadius: 6,
                                            margin: 0,
                                            ...(isGeneralPermission(status, null, remarks) ? { color: "#ffffff", backgroundColor: "#4F46E5", borderColor: "#4338CA" } : {})
                                        }}
                                    >
                                        {remarks === "Grace - Early Out"
                                            ? "Early Out"
                                            : remarks === "Grace - Late In"
                                                ? "Late In"
                                                : remarks || "No Record"}
                                    </Tag>
                                </div>

                                {(record?.onduty_status === "PENDING" || record?.onduty_status === "Pending" || record?.is_pending_onduty) && (
                                    <div className="d-flex align-items-center gap-1.5">
                                        <span style={{ fontWeight: 700, color: "#64748b", fontSize: 12 }}>On-Duty:</span>
                                        <Tag
                                            color="error"
                                            style={{ fontSize: 11.5, fontWeight: 800, padding: "2px 8px", borderRadius: 6, margin: 0 }}
                                        >
                                            ⏳ Pending Approval
                                        </Tag>
                                    </div>
                                )}
                            </div>

                            <ImproperLog record={record} employee={employee} />
                        </div>
                    );
                })()}
            </Modal>
        </div>
    );
}